"use client";
import { useState, useEffect } from "react";
import { Box, Typography, Container, Paper } from "@mui/material";
import Image from "next/image";
import ScrollReveal from "./ScrollReveal";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const FounderMemberSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [discountPercentage, setDiscountPercentage] = useState(20);

  useEffect(() => {
    try {
      const offersData = localStorage.getItem("offersData");
      if (offersData) {
        const parsedData = JSON.parse(offersData);
        const discount = parsedData?.data?.discountPercentage;
        if (discount !== undefined && discount !== null) {
          setDiscountPercentage(discount);
        }
      }
    } catch (error) {
      console.error(
        "Error reading discountPercentage from localStorage:",
        error,
      );
    }
  }, []);

  const benefitsRaw = t("founderMemberSection.benefits", {
    returnObjects: true,
  });
  const benefitsData = Array.isArray(benefitsRaw) ? benefitsRaw : [];

  const benefitIcons = [
    IMAGES.FMS_ONE_IMG,
    IMAGES.FMS_TWO_IMG,
    IMAGES.FMS_THREE_IMG,
    IMAGES.FMS_FOUR_IMG,
  ];

  const benefits = benefitsData.map((benefit, i) => ({
    icon: benefitIcons[i],
    alt: benefit.alt,
    title: benefit.title,
    description: benefit.description,
  }));

  return (
    <Box
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        px: { xs: 2, sm: 3, md: 4 },
        ml: { xs: "-2%", sm: "-2%", md: 0 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <ScrollReveal delay={0.1} duration={1.0}>
          <Box
            sx={{
              textAlign: isArabic
                ? { xs: "right", sm: "center" }
                : { xs: "left", sm: "center" },
              mb: { xs: 4, sm: 6, md: 7 },
            }}
          >
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: "bold",
                color: "#111827",
                mb: 1,
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                lineHeight: 1.3,
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              {t("founderMemberSection.title")}
            </Typography>
            <Typography
              sx={{
                color: "#4B5563",
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                maxWidth: { xs: "100%", sm: "80%", md: "800px" },
                mx: { xs: 0, sm: "auto" },
                lineHeight: 1.6,
                fontFamily: "var(--font-outfit)",
                fontWeight: 400,
              }}
            >
              {t("founderMemberSection.subtitle")}
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Benefits Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: { xs: 2.5, sm: 3.5, md: 4 },
          }}
        >
          {benefits.map((benefit, index) => (
            <ScrollReveal key={index} delay={0.2 + index * 0.1} duration={1.0}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3, md: 3.5 },
                  borderRadius: 2.5,
                  backgroundColor: "white",
                  textAlign: isArabic
                    ? { xs: "right", sm: "center" }
                    : { xs: "left", sm: "center" },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isArabic
                    ? { xs: "flex-start", sm: "center" }
                    : { xs: "flex-start", sm: "center" },
                  gap: { xs: 1.5, sm: 2 },
                  height: "100%",
                  transition: "all 0.3s ease",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: { xs: 60, sm: 72 },
                    height: { xs: 60, sm: 72 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isArabic
                      ? { xs: "flex-end", sm: "center" }
                      : { xs: "flex-start", sm: "center" },
                  }}
                >
                  <Image
                    src={benefit.icon}
                    alt={benefit.alt}
                    width={48}
                    height={48}
                    style={{ objectFit: "contain" }}
                  />
                </Box>

                {/* Title */}
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#111827",
                    fontSize: {
                      xs: "0.95rem",
                      sm: "1.1rem",
                      md: "1.1rem",
                      lg: "0.9rem",
                      xl: "1.1rem",
                    },
                    lineHeight: 1.3,
                    fontFamily: "var(--font-outfit)",
                  }}
                >
                  {benefit.title}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    color: "#4B5563",
                    fontSize: {
                      xs: "0.8rem",
                      sm: "0.7rem",
                      md: "0.7rem",
                      lg: "0.7rem",
                      xl: "0.9rem",
                    },
                    lineHeight: 1.6,
                    mt: -0.5,
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 400,
                  }}
                >
                  {benefit.description}
                </Typography>
              </Paper>
            </ScrollReveal>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FounderMemberSection;
